import React, { useState } from 'react'
import { useQueryClient } from 'react-query'
import { useDashboardGridRows, useCreateDashboardGridRow, useMoveGridRow } from 'api/hooks/use-dashboard-grid-rows'
import GridRow from './grid-row'
import AddGridRow from './add-grid-row'
import { sumBy } from 'lodash'
import CardLoading from 'shared/card-loading'
import CardError from 'shared/card-error'
import { useClearOnOutsideClick } from 'shared/use-clear-on-outside-click'

function DashboardGrid({ dashboardId, sectionId, params, canEdit = false, isPreviewMode = false }) {
  const queryClient = useQueryClient()
  const GRID_COLUMN_LIMIT = 20
  const [selectedChartId, setSelectedChartId] = useState(null)
  useClearOnOutsideClick(Boolean(selectedChartId), () => setSelectedChartId(null))
  
  const {
    data: gridRows = [],
    isSuccess: isGridRowsSuccess,
    isError: isGridRowsError,
    isFetching: isGridRowsFetching,
    refetch: refetchGridRows
  } = useDashboardGridRows({
    id: dashboardId,
    options: { 
      enabled: !!dashboardId,
      refetchOnWindowFocus: false 
    }
  })

  const totalColumnCount = sumBy(gridRows, 'column_count')
  const remainingColumnCapacity = GRID_COLUMN_LIMIT - totalColumnCount

  const { mutate: createGridRow, isLoading: isCreatingGridRow } = useCreateDashboardGridRow({
    id: dashboardId,
    options: {
      onSuccess: () => {
        queryClient.invalidateQueries([`dashboards/${dashboardId}/grid_rows`])
      }
    }
  })

  const { mutate: moveGridRow } = useMoveGridRow({
    dashboardId,
    options: {}
  })

  const handleAddRow = (columnCount) => {
    createGridRow({ column_count: columnCount })
  }

  const handleMoveUp = (gridRow) => {
    const currentPosition = gridRow.row_position
    const newPosition = Math.max(0, currentPosition - 1)
    moveGridRow({ id: gridRow.id, new_position: newPosition })
  }

  const handleMoveDown = (gridRow) => {
    const currentPosition = gridRow.row_position
    const maxPosition = gridRows.length - 1
    const newPosition = Math.min(maxPosition, currentPosition + 1)
    moveGridRow({ id: gridRow.id, new_position: newPosition })
  }

  if (isGridRowsError) {
    return (
      <CardError
        title="Couldn't load this dashboard"
        onRetry={() => refetchGridRows()}
        isRetrying={isGridRowsFetching}
      />
    )
  }

  if (!isGridRowsSuccess) {
    return <CardLoading label="Loading dashboard…" />
  }

  return (
    <div>
        {gridRows.map((gridRow, index) => (
          <GridRow
            key={gridRow.id}
            gridRow={gridRow}
            dashboardId={dashboardId}
            sectionId={sectionId}
            params={params}
            onMoveUp={handleMoveUp}
            onMoveDown={handleMoveDown}
            isFirst={index === 0}
            isLast={index === gridRows.length - 1}
            canEdit={canEdit}
            isPreviewMode={isPreviewMode}
            selectedChartId={selectedChartId}
            onSelectChart={setSelectedChartId}
          />
        ))}

        {canEdit && !isPreviewMode && (
          <AddGridRow
            onAddRow={handleAddRow}
            isLoading={isCreatingGridRow}
            remainingCapacity={remainingColumnCapacity}
            totalColumnCount={totalColumnCount}
          />
        )}
    </div>
  )
}

export default DashboardGrid